import { UploadedFileType } from '@/graphql/generated/schemas'

import {
  INCIDENT_ATTACHMENT_EXTENSIONS,
  INCIDENT_ATTACHMENT_MIMES,
  MEDIA_EXTENSIONS,
  MEDIA_MIMES,
  UNSIGNED_EXTENSIONS,
} from './fileTypeLists'

export interface FileTypePolicyI {
  // '*' allows any extension / MIME type
  extensions: string[] | '*'
  mimes: string[] | '*'
  shouldUseMagicNumber: boolean
  // Extensions with no reliable magic number (text files and OLE/OOXML
  // containers, which report as `zip`). These fall back to filename + MIME
  // validation even when shouldUseMagicNumber is on.
  unsignedExtensions?: string[]
}

// null means the file type is not validated in the web-app.
export const FILE_TYPE_POLICIES: Record<
  UploadedFileType,
  FileTypePolicyI | null
> = {
  // TODO: Implement validation for checkpoint images
  [UploadedFileType.CheckpointImage]: null,
  [UploadedFileType.CsvFile]: {
    // We cannot use validation based on magic number for text-based files
    // Reference: <https://github.com/sindresorhus/file-type?tab=readme-ov-file#file-type>
    extensions: ['csv'],
    mimes: ['text/csv'],
    shouldUseMagicNumber: false,
  },
  [UploadedFileType.FloorPlan]: {
    extensions: ['png'],
    mimes: ['image/png'],
    shouldUseMagicNumber: true,
  },
  // Unused in the web-app for now
  [UploadedFileType.GridAnalysisVideo]: null,
  [UploadedFileType.IncidentImage]: {
    extensions: INCIDENT_ATTACHMENT_EXTENSIONS,
    mimes: INCIDENT_ATTACHMENT_MIMES,
    shouldUseMagicNumber: true,
    unsignedExtensions: UNSIGNED_EXTENSIONS,
  },
  [UploadedFileType.IncidentNoteMedia]: {
    extensions: INCIDENT_ATTACHMENT_EXTENSIONS,
    mimes: INCIDENT_ATTACHMENT_MIMES,
    shouldUseMagicNumber: false,
    unsignedExtensions: UNSIGNED_EXTENSIONS,
  },
  // The logo of a Quick Report form, not the images submitted through it —
  // those upload as IncidentImage. Left unvalidated as it was before.
  [UploadedFileType.QuickReportImage]: null,
  [UploadedFileType.StandardOperatingProcedure]: {
    extensions: ['pdf'],
    mimes: ['application/pdf'],
    shouldUseMagicNumber: true,
  },
  // Unused
  [UploadedFileType.UserAvatar]: null,
  [UploadedFileType.UserFeedbackMedia]: {
    extensions: MEDIA_EXTENSIONS,
    mimes: MEDIA_MIMES,
    shouldUseMagicNumber: false,
  },
  // Unused
  [UploadedFileType.UserPhoto]: null,
  // Unused in the web-app, consider implementing after pulling that into shared module.
  [UploadedFileType.VideoClip]: null,
  [UploadedFileType.VoiceClip]: {
    // Uploaded voice messages don't have file extension
    extensions: '*',
    mimes: ['audio/wav', 'audio/x-wav'],
    shouldUseMagicNumber: true,
  },
}

export const getFileTypePolicy = (fileType: UploadedFileType) =>
  FILE_TYPE_POLICIES[fileType]
